import * as color from '@constant/color';
import {
  IIyagigoMenu,
  TIyagigoMainMenu,
  TIyagigoSubMenu,
  TStudyPlanStep,
  IStudyRouteStep,
} from '@datatype/iyagigo/type';
import { EApiLanguageCode, EApiLanguageLevel, EApiStudyTimeGoal } from '@datatype/iyagigo/api-type';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';

export const IS_BETA_SERVICE = false;

export const CLASSROOM_EXPRESSION_GROUP_NAME: Record<string, string> = {
  greeting: '인사',
  number: '숫자',
  'calendar-time': '시간과 날짜',
};

export const CLASSROOM_CONVERSATION_GROUP_NAME: Record<string, string> = {
  restaurant: '식당',
  store: '쇼핑',
  transport: '교통',
  hotel: '숙소',
  direction: '길 묻기',
  sightseeing: '관광',
  airport: '공항',
  trouble: '도움 구하기',
};

// 테스트용: 모바일 모킹 (로컬에서 모바일 UI 테스트)
export const IS_MOBILE_MOCKING = false;

// AI 말하기 자유 주제. 서버 task가 아니라 주제 없이 시작하는 진입점이라 상수로 둔다.
export const FREE_TALK_SUBJECT = 'free-talk';

// 무한스크롤 페이지당 fetch 개수
export const INFINITE_SCROLL_PER_PAGE = 50;

// ############### subscription wall (cloze quota) ###############
// 비회원/무료회원 일일 cloze 한도 (구독 Wall — quota 트리거). 구독자는 무제한.
export const GUEST_VOCABULARY_CLOZE_QUOTA = 5;
export const GUEST_GRAMMAR_CLOZE_QUOTA = 2;

// 비회원 일일 단어집 한도 (회원가입 Wall — quota 트리거)
export const GUEST_VOCABULARY_BOOK_QUOTA = 7;

// 단어집 free 일차 정책 (책의 order 기준)
// - 비회원/가입자 동일: order <= 3 만 무료 (4일차부터 잠금)
// 가입의 학습량 보상 폐지 (잠수함 패치, cloze 정책과 일관)
export const GUEST_VOCABULARY_BOOK_FREE_ORDER = 3;
export const MEMBER_VOCABULARY_BOOK_FREE_ORDER = 3;

// application download toast
export const IYAGIGO_APPLICATION_DOWNLOAD_DISMISS_KEY = 'iyagigo:application-download-dismiss-key';
export const IYAGIGO_APPLICATION_DOWNLOAD_CLOSE_DISMISS_KEY = 'iyagigo:application-download-close-dismiss-key';
// notice (공지사항: 이벤트, 점검, 출시 공지 등)
// ...동안 보지않기 (X 닫기와 다름)
export const IYAGIGO_NOTICE_DISMISS_KEY = 'iyagigo:notice-dismiss-key';
// X 닫기 (...동안 보지않기와 다름)
export const IYAGIGO_NOTICE_CLOSE_DISMISS_KEY = 'iyagigo:notice-close-dismiss-key';
export const IYAGIGO_IOS_STORE_URL = 'https://apps.apple.com/app/id6759956246';
export const IYAGIGO_ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.iyagigo';

// today data initialization
export const IYAGIGO_INITIAL_TODAY_DATE_KEY = 'iyagigo:initial-today-date';
export const IYAGIGO_TODAY_INIT_DATA_MIN_TIME = 1500;
export const IYAGIGO_TODAY_INIT_DATA_MAX_TIME = 5000;

// ############### study plan (첫 학습 전 학습 계획) ###############
// 질문 문구/옵션 등 UI 상수는 component/iyagigo/study-plan/index.tsx에 있음 (개발 완료 후 이쪽으로 이동 예정)
//
export const IYAGIGO_STUDY_PLAN_STEPS: TStudyPlanStep[] = [
  'account-choice',
  'introduction',
  'source-channel',
  'study-motivation',
  'language-level',
  'study-time-goal',
];

const vocabularyBookLearningRoute: IStudyRouteStep = {
  title: '단어집 외우기',
  description: '표현력을 넓히는 어휘를 익혀요',
  path: IYAGIGO_PATH.VOCABULARY_BOOK_HOME_PATH,
  attendanceCheckKey: 'isVocabularyBookCheck',
};

const clozeLearningRoute: IStudyRouteStep = {
  title: '간단한 문장 빈칸 채우기',
  description: '단어와 문법을 골라 문장을 완성해요',
  path: IYAGIGO_PATH.CLOZE_HOME_PATH,
  attendanceCheckKey: 'isClozeCheck',
};

const grammarLearningRoute: IStudyRouteStep = {
  title: '문법 배우기',
  description: '일본어 문장의 기본 틀을 익혀요',
  path: IYAGIGO_PATH.CLASSROOM_HOME_PATH,
  attendanceCheckKey: 'isGrammarCheck',
  classroomSubMenu: 'grammar',
};

const characterLearningRoute: IStudyRouteStep = {
  title: '히라가나·가타카나 한 행 익히기',
  description: '일본어 글자부터 천천히 시작해요',
  path: IYAGIGO_PATH.CLASSROOM_HOME_PATH,
  attendanceCheckKey: 'isCharacterCheck',
  classroomSubMenu: 'character',
};

const basicExpressionLearningRoute: IStudyRouteStep = {
  title: '기본표현 익히기',
  description: '꼭 알아둘 기본 표현을 익혀요',
  path: IYAGIGO_PATH.CLASSROOM_HOME_PATH,
  attendanceCheckKey: 'isClassroomExpressionCheck',
  classroomSubMenu: 'expression',
};

const conversationLearningRoute: IStudyRouteStep = {
  title: '생활회화 익히기',
  description: '자주 만나는 장면의 대화를 익혀요',
  path: IYAGIGO_PATH.CLASSROOM_HOME_PATH,
  attendanceCheckKey: 'isConversationCheck',
  classroomSubMenu: 'conversation',
};

/* 기존 Clip 학습 경로 보존
const miniStoryLearningRoute: IStudyRouteStep = {
  title: '간단한 이야기 읽기',
  description: '짧은 대화를 보며 표현을 익혀요',
  path: IYAGIGO_PATH.CLIP_HOME_PATH,
  attendanceCheckKey: 'isMiniStoryCheck',
  clipSubMenu: 'mini-story',
};
*/

/* 기존 Clip 학습 경로 보존
const authenticExpressionLearningRoute: IStudyRouteStep = {
  title: '생생표현 하나 익히기',
  description: '일상에서 쓰는 자연스러운 표현을 익혀요',
  path: IYAGIGO_PATH.CLIP_HOME_PATH,
  attendanceCheckKey: 'isAuthenticExpressionCheck',
  clipSubMenu: 'authentic-expression',
};
*/

const elementaryStudyRoutes = [grammarLearningRoute, vocabularyBookLearningRoute, clozeLearningRoute, conversationLearningRoute];
// 기존 경로: [vocabularyBookLearningRoute, clozeLearningRoute, miniStoryLearningRoute, grammarLearningRoute]

const advancedStudyRoutes = [vocabularyBookLearningRoute, clozeLearningRoute, grammarLearningRoute, conversationLearningRoute];
// 기존 경로: [vocabularyBookLearningRoute, clozeLearningRoute, miniStoryLearningRoute, authenticExpressionLearningRoute]

export const JAPANESE_STUDY_ROUTES: Record<EApiLanguageLevel, IStudyRouteStep[]> = {
  [EApiLanguageLevel.BEGINNER]: [
    characterLearningRoute,
    basicExpressionLearningRoute,
    grammarLearningRoute,
    vocabularyBookLearningRoute,
  ],
  [EApiLanguageLevel.BASIC]: [grammarLearningRoute, conversationLearningRoute, vocabularyBookLearningRoute, clozeLearningRoute],
  // 기존 경로: [grammarLearningRoute, basicExpressionLearningRoute, vocabularyBookLearningRoute, clozeLearningRoute]
  [EApiLanguageLevel.ELEMENTARY]: elementaryStudyRoutes,
  [EApiLanguageLevel.INTERMEDIATE]: advancedStudyRoutes,
  [EApiLanguageLevel.ADVANCED]: advancedStudyRoutes,
};

const englishBeginnerStudyRoutes = [basicExpressionLearningRoute, conversationLearningRoute, vocabularyBookLearningRoute, clozeLearningRoute];
// 기존 경로: [vocabularyBookLearningRoute, clozeLearningRoute, miniStoryLearningRoute, authenticExpressionLearningRoute]

const englishAdvancedStudyRoutes = [vocabularyBookLearningRoute, clozeLearningRoute, basicExpressionLearningRoute, conversationLearningRoute];
// 기존 경로: [vocabularyBookLearningRoute, clozeLearningRoute, miniStoryLearningRoute, authenticExpressionLearningRoute]

export const ENGLISH_STUDY_ROUTES: Record<EApiLanguageLevel, IStudyRouteStep[]> = {
  [EApiLanguageLevel.BEGINNER]: englishBeginnerStudyRoutes,
  [EApiLanguageLevel.BASIC]: englishBeginnerStudyRoutes,
  [EApiLanguageLevel.ELEMENTARY]: englishAdvancedStudyRoutes,
  [EApiLanguageLevel.INTERMEDIATE]: englishAdvancedStudyRoutes,
  [EApiLanguageLevel.ADVANCED]: englishAdvancedStudyRoutes,
};

export const getStudyRoute = (language: EApiLanguageCode, level?: EApiLanguageLevel | null): IStudyRouteStep[] => {
  const currentLevel = level && JAPANESE_STUDY_ROUTES[level] ? level : EApiLanguageLevel.BASIC;
  return language === EApiLanguageCode.ENGLISH
    ? ENGLISH_STUDY_ROUTES[currentLevel]
    : JAPANESE_STUDY_ROUTES[currentLevel];
};

// 사용자는 자기가 '초급'인지 '중급'인지 모른다.
// text = 위치 표시(고민 없이 감으로) / description = 판단 기준(구체적 행동)으로 역할을 분리.
// 학습언어 선택 (홈 상단 칩 → study-language 모달). 국기는 모달에서 별도로 렌더한다.
export const IYAGIGO_STUDY_LANGUAGE_OPTIONS = [
  { value: EApiLanguageCode.JAPANESE, text: '일본어', description: '일본어를 공부해요' },
  { value: EApiLanguageCode.ENGLISH, text: '영어', description: '영어를 공부해요' },
];

export const IYAGIGO_STUDY_PLAN_LANGUAGE_LEVEL_OPTIONS = [
  { value: EApiLanguageLevel.BEGINNER, text: '입문', description: '처음 시작해요' },
  { value: EApiLanguageLevel.BASIC, text: '기초', description: '단어와 인사말을 알아요' },
  { value: EApiLanguageLevel.ELEMENTARY, text: '초급', description: '간단한 문장을 이해해요' },
  { value: EApiLanguageLevel.INTERMEDIATE, text: '중급', description: '일상적인 대화가 가능해요' },
  { value: EApiLanguageLevel.ADVANCED, text: '고급', description: '복잡한 내용도 이해해요' },
];

// 시간이 늘수록 콘텐츠를 하나씩 얹는 누적 구조.
// description에 소요시간을 쓰면 라벨(총 시간)과 충돌하므로 '+ 콘텐츠'만 표기한다.
export const IYAGIGO_STUDY_PLAN_STUDY_TIME_GOAL_OPTIONS = [
  { value: EApiStudyTimeGoal.MINIMAL, text: '5분', description: '<strong>단어집</strong>을 가볍게 봐요' },
  { value: EApiStudyTimeGoal.MEDIUM, text: '10분', description: '+ <strong>빈칸문제</strong>까지 꾸준히 풀어요' },
  {
    value: EApiStudyTimeGoal.CONSIDERABLE,
    text: '20분',
    description: '+ <strong>기본 학습</strong>을 하나 더 이어가요',
    // 기존 Clip 설명: '+ <strong>간편이야기</strong>까지 알차게 읽어요'
  },
  {
    value: EApiStudyTimeGoal.MAXIMAL,
    text: '30분 이상',
    description: '+ <strong>생활회화</strong>까지 알차게 익혀요',
    // 기존 Clip 설명: '+ <strong>생생표현</strong>까지 열심히 챙겨요'
  },
];

// iyagigo
export const IG_BEGIN_MENUS: {
  type: IIyagigoMenu['main'];
  menu: string;
  text: string;
  icon: { icon: string; size: string; kind: string; color: string };
}[] = [
  // {
  //   menu: 'subscription-all',
  //   text: '많이 읽은 글',
  //   icon: { icon: 'bookmark', size: 'medium', kind: 'filled', color: color.WHITE_1 },
  // },
  {
    type: 'begin',
    menu: 'gana',
    text: '히라가나/가타가나',
    icon: { icon: 'today', size: 'medium', kind: 'outlined', color: color.WHITE_1 },
  },
  {
    type: 'begin',
    menu: 'gana',
    text: '기초일본어',
    icon: { icon: 'search', size: 'medium', kind: 'outlined', color: color.WHITE_1 },
  },
  {
    type: 'begin',
    menu: 'gana',
    text: '오늘의 일본어',
    icon: { icon: 'help', size: 'medium', kind: 'outlined', color: color.WHITE_1 },
  },
];

export const IG_ENGLISH_CLOZE_MENUS: {
  type: IIyagigoMenu['main'];
  menu: IIyagigoMenu['cloze'];
  text: string;
}[] = [
  { type: 'cloze', menu: 'vocabulary', text: '어휘연습' },
  { type: 'cloze', menu: 'grammar', text: '문법연습' },
  // { type: 'cloze', menu: 'sentence', text: '문장 만들기' },
];

export const IG_JAPANESE_CLOZE_MENUS: {
  type: IIyagigoMenu['main'];
  menu: IIyagigoMenu['cloze'];
  text: string;
}[] = [
  { type: 'cloze', menu: 'vocabulary', text: '어휘연습' },
  { type: 'cloze', menu: 'grammar', text: '문법연습' },
];

// 일본어 교실(classroom) = 입문(히라가나/기본표현/기초문법). 생생표현/간편이야기는 IG_*_CLIP_MENUS로 분리됨.
export const IG_JAPANESE_CLASSROOM_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['classroom'];
  text: string;
}[] = [
  { type: 'classroom', menu: 'character', text: '글자' },
  { type: 'classroom', menu: 'grammar', text: '문법' },
  { type: 'classroom', menu: 'expression', text: '기본표현' },
  { type: 'classroom', menu: 'conversation', text: '생활회화' },
];

// 영어 교실은 문법·기본표현·생활회화를 제공한다. 메인 교실 탭의 노출 환경은 Navigator에서 제어한다.
export const IG_ENGLISH_CLASSROOM_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['classroom'];
  text: string;
}[] = [
  { type: 'classroom', menu: 'grammar', text: '문법' },
  { type: 'classroom', menu: 'expression', text: '기본표현' },
  { type: 'classroom', menu: 'conversation', text: '생활회화' },
];

// 일상표현(clip) = 매일 발행 콘텐츠(생생표현 영상 / 간편이야기 대화)
export const IG_JAPANESE_CLIP_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['clip'];
  text: string;
}[] = [
  { type: 'clip', menu: 'authentic-expression', text: '생생표현' },
  { type: 'clip', menu: 'mini-story', text: '간편이야기' },
];

export const IG_ENGLISH_CLIP_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['clip'];
  text: string;
}[] = [
  { type: 'clip', menu: 'authentic-expression', text: '생생표현' },
  { type: 'clip', menu: 'mini-story', text: '간편이야기' },
];

export const IG_VOCABULARY_BOOK_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['vocabulary-book'];
  text: string;
}[] = [
  // 시험단어집(시험준비) 숨김 — 단어집/단어노트 축으로 재편
  // { type: 'vocabulary-book', menu: 'vocabulary-book-test', text: '시험준비' },
  { type: 'vocabulary-book', menu: 'vocabulary-book-study', text: '단어집' },
  { type: 'vocabulary-book', menu: 'vocabulary-book-note', text: '단어노트' },
];

export const IG_ENGLISH_TEST_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['test'];
  text: string;
}[] = [];

export const IG_JAPANESE_TEST_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['test'];
  text: string;
}[] = [];

export const IG_HUB_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['hub'];
  text: string;
  icon: { icon: string; size: string; kind: string; color: string };
}[] = [
  {
    type: 'hub',
    menu: 'account',
    // text: '계정·구독관리',
    text: '계정관리',
    icon: { icon: 'mail', size: 'medium', kind: 'outlined', color: color.WHITE_1 },
  },
  {
    type: 'hub',
    menu: 'help',
    text: '도움말',
    icon: { icon: 'mail', size: 'medium', kind: 'outlined', color: color.WHITE_1 },
  },
  {
    type: 'hub',
    menu: 'contact',
    text: '문의 및 의견',
    icon: { icon: 'mail', size: 'medium', kind: 'outlined', color: color.WHITE_1 },
  },
  {
    type: 'hub',
    menu: 'application-metadata',
    text: '앱 정보',
    icon: { icon: 'mail', size: 'medium', kind: 'outlined', color: color.WHITE_1 },
  },
  {
    type: 'hub',
    menu: 'subscription',
    text: '구독',
    icon: { icon: 'mail', size: 'medium', kind: 'outlined', color: color.WHITE_1 },
  },
];

export const IG_STUDIO_MENUS: {
  type: TIyagigoMainMenu;
  menu: TIyagigoSubMenu['studio'];
  text: string;
}[] = [
  {
    type: 'studio',
    menu: 'statistic',
    text: '학습통계',
  },
  {
    type: 'studio',
    menu: 'configuration',
    text: '학습설정',
  },
];
